import React, { useEffect, useId, useRef, useState } from "react";
import {
  Plus,
  Trash2,
  MessageSquare,
  Pencil,
  Loader2,
  ChevronDown,
  ChevronRight,
  Archive,
  ArchiveRestore,
  Search,
  X,
} from "lucide-react";
import api from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import {
  PERMANENT_DELETE_CONFIRM_PHRASE,
  isPermanentDeleteConfirmPhrase,
} from "@/hooks/useChatRecentsHistory";
import { toast } from "@/lib/toast.js";

function formatRelativeTime(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function getSessionTitle(session) {
  if (session.title) return session.title;
  if (session.firstMessage) {
    const trimmed = session.firstMessage.slice(0, 50);
    return trimmed.length < session.firstMessage.length ? trimmed + "..." : trimmed;
  }
  return `Session ${new Date(session.started_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
}

/**
 * Recents list for the left app nav / mobile sheet.
 * Presentation only — history network state is owned by ChatAssistant via useChatRecentsHistory.
 */
export default function ChatSidebar({
  sessions,
  activeSessionId,
  onSelectSession,
  onDeleteSession,
  onArchiveSession,
  onRestoreSession,
  onSessionTitleUpdated,
  loading,
  loadingMore = false,
  sessionsLoadError,
  loadMoreError = null,
  onRetrySessions,
  onLoadMore,
  hasMore = false,
  view = "active",
  onViewChange,
  searchQuery = "",
  onSearchQueryChange,
  onClearSearch,
  mutationSessionId = null,
  /** When true, render compact (icon-rail) — Layout collapsed. */
  navCollapsed = false,
  /** "nav" = capped list in left nav; "overlay" = full-height drawer list (mobile). */
  layout = "nav",
  /** §54.3 — Recents list open (persisted by ChatAssistant). Overlay layout always shows the list. */
  drawerOpen = true,
  /** Nav only — omit on mobile overlay (ephemeral Sheet). */
  onDrawerOpenChange = undefined,
}) {
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingTitle, setEditingTitle] = useState("");
  const [savingId, setSavingId] = useState(null);
  const searchInputRef = useRef(null);
  const deleteTriggerRef = useRef(null);
  const deleteDescriptionId = useId();
  const confirmInputId = useId();
  const viewGroupId = useId();

  const isArchivedView = view === "archived";
  const confirmMatches = isPermanentDeleteConfirmPhrase(deleteConfirmText);
  const hasSearch = Boolean(searchQuery && searchQuery.trim());

  useEffect(() => {
    if (!deleteTargetId) {
      setDeleteConfirmText("");
      setDeleteSubmitting(false);
    }
  }, [deleteTargetId]);

  const closeDeleteDialog = () => {
    setDeleteTargetId(null);
    setDeleteConfirmText("");
    setDeleteSubmitting(false);
    requestAnimationFrame(() => deleteTriggerRef.current?.focus?.());
  };

  const handleConfirmDelete = async () => {
    if (!deleteTargetId || !confirmMatches || deleteSubmitting) return;
    setDeleteSubmitting(true);
    try {
      await onDeleteSession(deleteTargetId, deleteConfirmText);
      closeDeleteDialog();
    } catch {
      setDeleteSubmitting(false);
    }
  };

  const handleStartEdit = (e, session) => {
    e.stopPropagation();
    setEditingId(session.id);
    setEditingTitle(getSessionTitle(session));
  };

  const handleSaveEdit = async (sessionId) => {
    const trimmed = editingTitle.trim();
    if (!trimmed) {
      setEditingId(null);
      return;
    }
    const previous = sessions.find((s) => s.id === sessionId);
    const previousTitle = previous ? getSessionTitle(previous) : "";
    if (trimmed === previousTitle) {
      setEditingId(null);
      return;
    }
    setSavingId(sessionId);
    try {
      const res = await api.chat.updateSession(sessionId, { title: trimmed });
      const nextTitle =
        typeof res.data?.title === "string" && res.data.title.trim()
          ? res.data.title.trim()
          : trimmed;
      onSessionTitleUpdated?.(sessionId, nextTitle);
      toast.success("Chat renamed");
    } catch {
      toast.error("Couldn't rename chat");
    } finally {
      setSavingId(null);
      setEditingId(null);
    }
  };

  const handleEditKeyDown = (e, sessionId) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSaveEdit(sessionId);
    } else if (e.key === "Escape") {
      setEditingId(null);
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onClearSearch?.();
    }
  };

  const emptyCopy = hasSearch
    ? "No matching conversations"
    : isArchivedView
      ? "No archived chats"
      : "No recent chats";

  if (navCollapsed) {
    return null;
  }

  return (
    <>
      <div
        className={cn(
          "mt-1 mb-2 px-2 flex flex-col min-h-0",
          layout === "overlay" ? "flex-1 h-full" : "shrink-0",
        )}
      >
        <div className="flex items-center justify-between gap-1 px-1 mb-1">
          {layout === "nav" && onDrawerOpenChange ? (
            <button
              type="button"
              className="flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-widest text-gray-400 hover:text-ad-primary"
              onClick={() => onDrawerOpenChange(!drawerOpen)}
              aria-label={drawerOpen ? "Collapse recents" : "Expand recents"}
              aria-expanded={drawerOpen}
            >
              {drawerOpen ? (
                <ChevronDown className="w-3 h-3" aria-hidden />
              ) : (
                <ChevronRight className="w-3 h-3" aria-hidden />
              )}
              Recents
            </button>
          ) : (
            <span className="text-[9px] font-bold uppercase tracking-widest text-gray-400">
              Recents
            </span>
          )}
        </div>

        {(layout === "overlay" || drawerOpen) && (
          <>
            <div
              className="flex rounded-md border border-ad-border bg-gray-50 p-0.5 mb-1.5"
              role="tablist"
              aria-label="Conversation history view"
              id={viewGroupId}
            >
              <button
                type="button"
                role="tab"
                aria-selected={!isArchivedView}
                className={cn(
                  "flex-1 rounded px-2 py-1 text-[10px] font-semibold transition-colors",
                  !isArchivedView
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-700",
                )}
                onClick={() => onViewChange?.("active")}
              >
                Active
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={isArchivedView}
                className={cn(
                  "flex-1 rounded px-2 py-1 text-[10px] font-semibold transition-colors",
                  isArchivedView
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-700",
                )}
                onClick={() => onViewChange?.("archived")}
              >
                Archived
              </button>
            </div>

            <div className="relative mb-1.5">
              <label htmlFor={`${viewGroupId}-search`} className="sr-only">
                Search conversation titles
              </label>
              <Search
                className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none"
                aria-hidden
              />
              <input
                id={`${viewGroupId}-search`}
                ref={searchInputRef}
                type="search"
                value={searchQuery}
                onChange={(e) => onSearchQueryChange?.(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                placeholder="Search titles"
                autoComplete="off"
                className="w-full h-7 pl-7 pr-7 rounded-md border border-ad-border bg-white text-[11px] text-gray-800 placeholder:text-gray-400 outline-none focus:border-ad-primary"
              />
              {hasSearch && (
                <button
                  type="button"
                  className="absolute right-1 top-1/2 -translate-y-1/2 p-1 rounded text-gray-400 hover:text-gray-700"
                  aria-label="Clear search"
                  onClick={() => onClearSearch?.()}
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>

            {/* Independent scroll — nav: capped; overlay: fills drawer. */}
            <div
              className={cn(
                "overflow-y-auto overscroll-contain space-y-0.5 min-h-0 pr-0.5",
                layout === "overlay" ? "flex-1 max-h-none" : "max-h-[180px]",
              )}
            >
              {loading ? (
                <div className="flex items-center justify-center py-4" aria-busy="true">
                  <div className="w-4 h-4 border-2 border-ad-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : sessionsLoadError ? (
                <div className="text-center py-3 px-1">
                  <p className="text-[10px] text-red-600 font-medium">{sessionsLoadError}</p>
                  {onRetrySessions && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2 h-6 text-[10px]"
                      onClick={onRetrySessions}
                    >
                      Retry
                    </Button>
                  )}
                </div>
              ) : sessions.length === 0 ? (
                <div className="text-center py-3 px-1">
                  <p className="text-[10px] text-gray-500">{emptyCopy}</p>
                </div>
              ) : (
                sessions.map((session) => {
                  const isActive = session.id === activeSessionId;
                  const mutating = mutationSessionId === session.id;
                  return (
                    <div
                      key={session.id}
                      style={isActive ? { backgroundColor: "#f0f7ff" } : undefined}
                      className={cn(
                        "group cursor-pointer transition-colors rounded-md px-2 py-1.5",
                        isActive ? "border border-[#bfdbfe]" : "border border-transparent hover:bg-gray-50",
                        mutating && "opacity-60",
                      )}
                      onClick={() => editingId !== session.id && onSelectSession(session.id)}
                    >
                      <div className="flex items-start gap-2">
                        <MessageSquare
                          className={cn(
                            "w-3 h-3 shrink-0 mt-0.5",
                            isActive ? "text-ad-primary" : "text-gray-400",
                          )}
                        />
                        <div className="flex-1 min-w-0">
                          {editingId === session.id ? (
                            <div className="flex items-center gap-1 flex-1 min-w-0">
                              <input
                                autoFocus
                                value={editingTitle}
                                onChange={(e) => setEditingTitle(e.target.value)}
                                onKeyDown={(e) => handleEditKeyDown(e, session.id)}
                                onBlur={() => handleSaveEdit(session.id)}
                                onClick={(e) => e.stopPropagation()}
                                disabled={savingId === session.id}
                                maxLength={100}
                                className="w-full bg-white border border-ad-primary rounded px-1.5 py-0.5 text-[10px] font-semibold text-gray-900 outline-none flex-1 min-w-0 disabled:opacity-50"
                              />
                              {savingId === session.id && (
                                <Loader2 className="w-3 h-3 text-ad-primary animate-spin flex-shrink-0" />
                              )}
                            </div>
                          ) : (
                            <div className="flex items-start justify-between gap-0.5 min-w-0">
                              <p
                                className={cn(
                                  "text-[11px] font-medium truncate min-w-0 flex-1",
                                  isActive ? "text-gray-900" : "text-gray-600",
                                )}
                              >
                                {getSessionTitle(session)}
                              </p>
                              <div className="flex items-center gap-0.5 shrink-0 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 focus-within:opacity-100">
                                <button
                                  type="button"
                                  className="p-1 rounded text-gray-400 hover:text-ad-primary transition-colors"
                                  onClick={(e) => handleStartEdit(e, session)}
                                  aria-label="Rename conversation"
                                  title="Rename"
                                  disabled={mutating}
                                >
                                  <Pencil className="w-3 h-3" />
                                </button>
                                {!isArchivedView ? (
                                  <button
                                    type="button"
                                    className="p-1 rounded text-gray-400 hover:text-ad-primary transition-colors"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onArchiveSession?.(session.id);
                                    }}
                                    aria-label="Archive conversation"
                                    title="Archive"
                                    disabled={mutating}
                                  >
                                    <Archive className="w-3 h-3" />
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    className="p-1 rounded text-gray-400 hover:text-ad-primary transition-colors"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onRestoreSession?.(session.id);
                                    }}
                                    aria-label="Restore conversation"
                                    title="Restore"
                                    disabled={mutating}
                                  >
                                    <ArchiveRestore className="w-3 h-3" />
                                  </button>
                                )}
                                <button
                                  type="button"
                                  className="p-1 rounded text-gray-400 hover:text-red-500 transition-colors"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteTriggerRef.current = e.currentTarget;
                                    setDeleteTargetId(session.id);
                                  }}
                                  aria-label="Delete conversation permanently"
                                  title="Delete permanently"
                                  disabled={mutating}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            </div>
                          )}
                          <p className="text-[9px] text-gray-400 font-medium mt-0.5">
                            {formatRelativeTime(session.last_message_at || session.started_at)}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {!loading && !sessionsLoadError && hasMore && (
              <div className="pt-1.5 px-0.5">
                {loadMoreError && (
                  <p className="text-[10px] text-red-600 text-center mb-1">{loadMoreError}</p>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full h-7 text-[10px]"
                  onClick={() => onLoadMore?.()}
                  disabled={loadingMore || Boolean(mutationSessionId)}
                  aria-label="Load more conversations"
                >
                  {loadingMore ? (
                    <span className="inline-flex items-center gap-1">
                      <Loader2 className="w-3 h-3 animate-spin" aria-hidden />
                      Loading…
                    </span>
                  ) : loadMoreError ? (
                    "Retry"
                  ) : (
                    "Load more"
                  )}
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      <AlertDialog
        open={Boolean(deleteTargetId)}
        onOpenChange={(open) => {
          if (!open) closeDeleteDialog();
        }}
      >
        <AlertDialogContent className="max-w-[min(100%,24rem)]">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete permanently?</AlertDialogTitle>
            <AlertDialogDescription id={deleteDescriptionId}>
              This cannot be undone. Messages and the conversation summary will be permanently
              removed. To hide a chat and restore it later, use Archive instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 py-1">
            <label htmlFor={confirmInputId} className="text-xs font-medium text-gray-700">
              Type {PERMANENT_DELETE_CONFIRM_PHRASE} to confirm
            </label>
            <Input
              id={confirmInputId}
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              autoComplete="off"
              spellCheck={false}
              aria-describedby={deleteDescriptionId}
              className="h-9 text-sm font-mono"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteSubmitting}>Cancel</AlertDialogCancel>
            <Button
              type="button"
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={!confirmMatches || deleteSubmitting}
              onClick={handleConfirmDelete}
            >
              {deleteSubmitting ? "Deleting…" : "Delete permanently"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/** New Chat control for the top of the left nav — calls the same startNewChat path via event. */
export function ChatNavNewChatButton({ collapsed, onNavigateToChat }) {
  return (
    <button
      type="button"
      onClick={onNavigateToChat}
      className={cn(
        "flex items-center gap-3 rounded-md text-xs font-medium transition-colors duration-150 w-full",
        "text-gray-700 hover:bg-gray-50 hover:text-gray-900 px-3 py-2",
      )}
      aria-label="New chat"
    >
      <span className="w-4 h-4 shrink-0 flex items-center justify-center rounded border border-ad-border bg-white">
        <Plus className="w-3 h-3 text-ad-primary" />
      </span>
      {!collapsed && <span>New chat</span>}
    </button>
  );
}
